import { pipe, sort, head, prop, isNil, isEmpty } from "ramda"
import { filterBy } from "@leeruniek/functies"
import { t } from "lib/core/i18n/root.i18"

import {
  UNDEFINED_PUPIL_APPROACH,
  ALL_OPTION_VALUE,
  SCORE_VALUABLE_GRADE,
  TEST_PROVIDER_GROUP_DISPLAYED_SCORE_FORMATS,
  TEST_PROVIDER_DISPLAYED_SCORE_FORMATS,
  TEST_PROVIDER_SUBJECTS_WITH_REFERENCE_LEVEL,
  LAYERS,
  OPTIONAL_SCORE_FORMATS_BY_PROVIDER,
  CITO_SCORE_FORMATS,
  DUTCH_LAST_NAME_PREFIXES_SORTED,
  TEST_PROVIDERS,
  STILL_SCORE_DIFF_LIMITS,
  EXPECTED_ABILITY_SCORE_DIFF_LIMITS,
  YEAR_TIMES,
  SUBJECTS,
  EXPECTED_IEP_ABILITY_SCORE_GROWTH_PER_TEST_PERIOD,
} from "lib/leeruniek/constants"
import { toDateTimeFormat } from "./time-helpers"

// Get the latest instruction group (approach) for a subjectId, or
// "undefined" if there is no approach assigned yet.
export const lastApproachFor = (subjectId) =>
  pipe(
    filterBy({
      subjectId: subjectId,
    }),
    sort((a, b) => {
      const aDate = a.dateChanged || a.dateCreated
      const bDate = b.dateChanged || b.dateCreated

      return new Date(bDate) - new Date(aDate)
    }),
    head,
    (value) => (value ? value : { approach: UNDEFINED_PUPIL_APPROACH }),
    prop("approach"),
  )

// Return true if the note is for the same approach as the `approach`
// parameter.
export const noteMatchesApproach = (approach) => (note) =>
  note.approach === approach ||
  (isNil(note.approach) && approach === ALL_OPTION_VALUE)

export const getLatestAverage = (yeargroup, allSubjects, subjectId = null) => {
  // FIXME: Call the prop `citoScores` everywhere
  let filteredScores = [...(yeargroup.citoScores || yeargroup.scores)]

  if (subjectId) {
    filteredScores = filteredScores.filter((s) => s.subjectId === subjectId)
  }
  const averages = filteredScores.sort((avg1, avg2) => {
    if (avg1.year === avg2.year) {
      return avg1.yearTime.localeCompare(avg2.yearTime)
    }

    return avg2.year.localeCompare(avg1.year)
  })

  let latestAverage = averages[0]

  if (!latestAverage) {
    latestAverage = {
      subjectId: allSubjects[0].id,
      year: `${yeargroup.calendarYear}/${yeargroup.calendarYear + 1}`,
      yearClassId: yeargroup.id,
      academicYear: yeargroup.grade,
      yearTime: "M",
    }
  }
  return latestAverage
}

export const shouldShowProjectedReferenceLevel = (
  grade,
  testCode,
  subjectCode,
  testProvider,
) => {
  return (
    !isNil(testProvider) &&
    grade >= 5 &&
    testCode !== "M5" &&
    testCode !== "E8" &&
    TEST_PROVIDER_SUBJECTS_WITH_REFERENCE_LEVEL[testProvider.name].includes(
      subjectCode,
    )
  )
}

export const getLatestScoreByKey = (scores, key, value) => {
  const [latest] = scores
    .filter((score) => score[key] === value)
    .sort((a, b) => new Date(b.dateTaken) - new Date(a.dateTaken))

  return latest ? latest : null
}

export const getSubjectIdByCode = (subjects, subjectCode) => {
  const subject = subjects.find((subject) => subject.shortLabel === subjectCode)
  return subject ? subject.id : null
}

export const nullifyApproach = (approach) => {
  // Helper function to transform approach as represented on frontend
  // to form suitable for backend.
  // On backend ALL_OPTION_VALUE is represented as NONE (NULL).
  if (approach === ALL_OPTION_VALUE) {
    return null
  } else {
    return approach
  }
}

export const shouldShowYearclassLink = (subGroupRoute) => (yearclass) =>
  yearclass.academicYear >= SCORE_VALUABLE_GRADE ||
  subGroupRoute !== "groupResults"

export const getAvailableScoreTypes = (
  scoreData,
  testProviderName,
  isRefLevelAvailable,
  layer,
) => {
  const scoreFormats = {
    [LAYERS.GROUP]:
      TEST_PROVIDER_GROUP_DISPLAYED_SCORE_FORMATS[testProviderName],
    [LAYERS.PUPIL]: TEST_PROVIDER_DISPLAYED_SCORE_FORMATS[testProviderName],
  }
  let returnFormats
  const optionalFormats = OPTIONAL_SCORE_FORMATS_BY_PROVIDER[testProviderName]
  if (isNil(optionalFormats) || isNil(scoreData) || isEmpty(scoreData)) {
    returnFormats = scoreFormats[layer]
  } else {
    returnFormats = scoreFormats[layer].reduce((acc, format) => {
      if (!optionalFormats.includes(format)) {
        return [...acc, format]
      } else {
        const isScoreFormatAvailable = Array.isArray(scoreData)
          ? scoreData.some((score) => !isNil(score.result[format]))
          : !isNil(scoreData.result[format])
        return isScoreFormatAvailable ? [...acc, format] : acc
      }
    }, [])
  }
  if (isRefLevelAvailable) {
    return returnFormats
  } else {
    return returnFormats.filter(
      (format) =>
        format !== CITO_SCORE_FORMATS.REFERENCE_LEVEL &&
        format !== CITO_SCORE_FORMATS.PROJECTED_REFERENCE_LEVEL,
    )
  }
}

// Return a Dutch last name with the longest possible known prefix removed
const lastNameWithoutPrefix = (name) => {
  const prefix = DUTCH_LAST_NAME_PREFIXES_SORTED.find((prefix) =>
    name.startsWith(`${prefix} `),
  )
  return isNil(prefix) ? name : name.slice(prefix.length + 1)
}

// Compare last names, ignoring known prefixes
export const compareLastNames = (a, b) => {
  return lastNameWithoutPrefix(a).localeCompare(lastNameWithoutPrefix(b))
}

export const getScoreGrowthClassName = (
  currentScore,
  previousScore,
  diff,
  testProvider,
) => {
  let min, max
  if (testProvider === TEST_PROVIDERS.IEP) {
    min = EXPECTED_ABILITY_SCORE_DIFF_LIMITS.MIN
    max = EXPECTED_ABILITY_SCORE_DIFF_LIMITS.MAX
  } else {
    min = STILL_SCORE_DIFF_LIMITS.MIN
    max = STILL_SCORE_DIFF_LIMITS.MAX
  }
  if (diff > max) {
    return "text-success"
  } else if (diff < min) {
    return "text-danger"
  }
}

export const getExpectedGrowth = (academicYear, testTimeExpected, subject) =>
  academicYear < 6 ||
  (academicYear === 6 &&
    testTimeExpected === YEAR_TIMES.MIDDLE &&
    (subject === SUBJECTS.RW || subject.shortLabel === SUBJECTS.RW))
    ? EXPECTED_IEP_ABILITY_SCORE_GROWTH_PER_TEST_PERIOD.BEFORE_GRADE_SIX
    : EXPECTED_IEP_ABILITY_SCORE_GROWTH_PER_TEST_PERIOD.GRADE_SIX_OR_LATER

export const reloadPage = () => window.location.reload()

export const getLastModifiedBy = (user, datetime) => {
  return `${t("last-modified-by")}: ${user}, ${toDateTimeFormat(datetime)}`
}
